// src/components/AutomationManagement/AutomationManagement.jsx

import React, { useState, useEffect } from "react";
import axios from "../../axios";
import ReportTable from "./ReportTable";

export default function AutomationManagement() {
    const [projects, setProjects] = useState([]);
    const [features, setFeatures] = useState([]);
    const [selectedProject, setSelectedProject] = useState("");
    const [selectedFeature, setSelectedFeature] = useState("");
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [loadingFeatures, setLoadingFeatures] = useState(false);

    // Fetch all projects once
    useEffect(() => {
        const fetchProjects = async () => {
            setLoadingProjects(true);
            try {
                const res = await axios.get("/automation-result/projects");
                setProjects(Array.isArray(res.data) ? res.data : []);
            } catch {
                setProjects([]);
            } finally {
                setLoadingProjects(false);
            }
        };
        fetchProjects();
    }, []);

    // Fetch features whenever a project is selected
    useEffect(() => {
        const fetchFeatures = async () => {
            if (!selectedProject) {
                setFeatures([]);
                setSelectedFeature("");
                return;
            }
            setLoadingFeatures(true);
            try {
                const res = await axios.get("/automation-result/features", {
                    params: { project: selectedProject },
                });
                setFeatures(Array.isArray(res.data) ? res.data : []);
            } catch {
                setFeatures([]);
            } finally {
                setLoadingFeatures(false);
            }
        };
        fetchFeatures();
    }, [selectedProject]);

    return (
        <div className="flex min-h-screen bg-gray-50">
            <main className="flex-1 px-4 py-6">
                <div className="bg-white p-4 rounded-xl shadow-lg">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
                        Automation Test Results
                    </h1>

                    {/* STEP 1: Project Selection */}
                    {!selectedProject ? (
                        <section className="mb-6">
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">
                                Select a Project
                            </h2>
                            {loadingProjects ? (
                                <div className="text-center text-blue-600 font-medium py-4">
                                    Loading projects…
                                </div>
                            ) : projects.length === 0 ? (
                                <div className="text-center text-gray-500 py-4">
                                    No projects available
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                                    {projects.map((proj) => (
                                        <button
                                            key={proj}
                                            onClick={() => {
                                                setSelectedProject(proj);
                                                setSelectedFeature("");
                                            }}
                                            className={`border rounded-lg p-2 w-full text-left transition ${
                                                selectedProject === proj
                                                    ? "bg-indigo-100 border-indigo-500 shadow-md text-indigo-700"
                                                    : "bg-white border-gray-200 hover:shadow-lg hover:ring-2 hover:ring-indigo-300"
                                            }`}
                                        >
                                            <p className="text-base sm:text-lg font-medium truncate">
                                                {proj}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </section>
                    ) : (
                        <section className="flex items-center justify-between bg-gray-100 rounded-lg p-3 mb-6">
                            <p className="text-lg text-gray-800">
                                Project:{" "}
                                <span className="font-semibold">{selectedProject}</span>
                            </p>
                            <button
                                onClick={() => {
                                    setSelectedProject("");
                                    setSelectedFeature("");
                                }}
                                className="text-sm px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                            >
                                Change Project
                            </button>
                        </section>
                    )}

                    {/* STEP 2: Feature Selection */}
                    {selectedProject && !selectedFeature ? (
                        <section className="mb-6">
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">
                                Select a Feature{" "}
                                <span className="text-sm text-gray-500">
                  (Project:{" "}
                                    <span className="text-gray-800 font-semibold">
                    {selectedProject}
                  </span>
                  )
                </span>
                            </h2>
                            {loadingFeatures ? (
                                <div className="text-center text-blue-600 font-medium py-4">
                                    Loading features…
                                </div>
                            ) : features.length === 0 ? (
                                <div className="text-center text-gray-500 py-4">
                                    No features available for this project
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                                    {features.map((feat) => (
                                        <button
                                            key={feat}
                                            onClick={() => setSelectedFeature(feat)}
                                            className={`border rounded-lg p-2 w-full text-left transition ${
                                                selectedFeature === feat
                                                    ? "bg-green-100 border-green-500 shadow-md text-green-700"
                                                    : "bg-white border-gray-200 hover:shadow-lg hover:ring-2 hover:ring-green-300"
                                            }`}
                                        >
                                            <p className="text-base sm:text-lg font-medium truncate">
                                                {feat}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </section>
                    ) : selectedProject && selectedFeature ? (
                        <section className="flex items-center justify-between bg-gray-100 rounded-lg p-3 mb-6">
                            <p className="text-lg text-gray-800">
                                Feature:{" "}
                                <span className="font-semibold">{selectedFeature}</span>
                            </p>
                            <button
                                onClick={() => setSelectedFeature("")}
                                className="text-sm px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                            >
                                Change Feature
                            </button>
                        </section>
                    ) : null}

                    {/* STEP 3: Results Table via ReportTable */}
                    {selectedProject && selectedFeature && (
                        <ReportTable
                            project={selectedProject}
                            feature={selectedFeature}
                        />
                    )}
                </div>
            </main>
        </div>
    );
}
